/**
 * Instance pages live under /a/<slug> (apps/web/app/a/[slug]/) so a
 * user-chosen bot name can never collide with a top-level route — an
 * instance named "articles" must not become unreachable the day we ship
 * an /articles page. Every link to an instance page is built with
 * `instanceHref`, and every component that reads the slug back out of the
 * URL goes through `instancePathSegments`, so the prefix exists in exactly
 * one place.
 *
 * Kept dependency-free so client components can import it without pulling
 * server-only modules (unlike lib/instance-utils.tsx).
 *
 * The runtime mirrors this in `instanceUrl` (apps/runtime/src/config.ts) —
 * change both together.
 */
export const INSTANCE_PATH_PREFIX = "a";

/** Href for an instance page: instanceHref("felix", "/desk") → "/a/felix/desk".
 *  `sub` is appended verbatim — include the leading "/" or "?". */
export function instanceHref(slug: string, sub = ""): string {
  return `/${INSTANCE_PATH_PREFIX}/${slug}${sub}`;
}

/** An organization's "agents working together" page. */
export function organizationAgentsHref(orgId: string): string {
  return `/organizations/${orgId}/agents`;
}

/** One run between an organization's agents, and the decision about its
 *  budget. The runtime builds the same path in `collaboration/ceiling.ts`
 *  (`runPageUrl`), on its own origin. */
export function organizationRunHref(orgId: string, runKey: string): string {
  return `${organizationAgentsHref(orgId)}/runs/${runKey}`;
}

/** An agent's Team page, open on its conversation with one colleague. */
export function colleaguesHref(slug: string, peerInstanceId: string): string {
  return instanceHref(slug, `/team?with=${peerInstanceId}`);
}

/** Path segments of an instance page with the prefix stripped —
 *  "/a/felix/desk/files" → ["felix", "desk", "files"]. Returns [] for
 *  non-instance paths so `segments[0]` is the slug or undefined, never "a". */
export function instancePathSegments(pathname: string): string[] {
  const segments = pathname.split("/").filter(Boolean);
  return segments[0] === INSTANCE_PATH_PREFIX ? segments.slice(1) : [];
}

/** `/a/<slug>/companion`: the agent's private chat and nothing else, for the
 *  browser companion's side panel to frame (apps/companion-extension). The
 *  extension builds the same path in src/panel.ts. */
export const COMPANION_VIEW_SEGMENT = "companion";

export function isCompanionView(pathname: string): boolean {
  return instancePathSegments(pathname)[1] === COMPANION_VIEW_SEGMENT;
}

/** Where an `/a/<slug>/<rest>` that is no instance page was meant to go: the
 *  platform route `/<rest>`, query kept. `rest` arrives decoded (Next's
 *  catch-all params), so each segment is encoded again, and empty segments
 *  are dropped: `/a/x//evil.example` must not become `//evil.example`, which
 *  a browser reads as another origin. See app/a/[slug]/[...rest]/route.ts for
 *  why the prefix gets there. */
export function pathOutsideInstance(rest: readonly string[], search: string): string {
  return `/${rest.filter((segment) => segment !== "").map(encodeURIComponent).join("/")}${search}`;
}

/** The instance page a pathname is on. Group is a chat sub-route, so it reads
 *  as "chat", and so does the companion view. */
export type ActivePage = "chat" | "desk" | "settings" | "apps" | "team";

export function deriveActivePage(pathname: string): ActivePage {
  const page = instancePathSegments(pathname)[1];
  if (page === "desk") return "desk";
  if (page === "settings") return "settings";
  if (page === "apps") return "apps";
  if (page === "team") return "team";
  return "chat";
}
